import React from "react";
import { ChevronLeft, ChevronRight, CheckCircle2, CircleDashed } from "lucide-react";
import { parseISO, format, differenceInCalendarDays } from "date-fns";
import { mdyShort } from "@/lib/dateFmt";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

/** Small chip + grey due-caption shown inside each SelectItem. */
function MonthMeta({ p }) {
  const due = p.due_to_post ? parseISO(p.due_to_post) : null;
  const daysLeft = due ? differenceInCalendarDays(due, new Date()) : null;
  const publishedPill = p.published ? (
    <span className="inline-flex items-center gap-0.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 text-[9px] font-black uppercase tracking-widest px-1.5 py-[1px]">
      <CheckCircle2 size={9} /> Published
    </span>
  ) : (
    <span className="inline-flex items-center gap-0.5 rounded-full border border-slate-300 bg-slate-50 text-slate-600 text-[9px] font-black uppercase tracking-widest px-1.5 py-[1px]">
      <CircleDashed size={9} /> Not Published
    </span>
  );
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {publishedPill}
      {due && (
        <span className="text-[10px] text-slate-500 tabular-nums">
          Due to post: {mdyShort(due)}
          {daysLeft != null && (
            <>, {daysLeft < 0 ? `${-daysLeft}d overdue` : daysLeft === 0 ? "due today" : `${daysLeft}d left`}</>
          )}
        </span>
      )}
    </div>
  );
}

function shiftMonth(id, delta) {
  // id = "mo-YYYY-MM"
  const m = id.match(/^mo-(\d{4})-(\d{2})$/);
  if (!m) return id;
  let y = parseInt(m[1], 10);
  let mo = parseInt(m[2], 10) + delta;
  while (mo > 12) { y += 1; mo -= 12; }
  while (mo < 1)  { y -= 1; mo += 12; }
  return `mo-${String(y).padStart(4,"0")}-${String(mo).padStart(2,"0")}`;
}

export default function MonthSelector({ months, value, onChange, published }) {
  if (!value) return null;
  const prev = () => onChange(shiftMonth(value, -1));
  const next = () => onChange(shiftMonth(value,  1));
  const inList = months.some((m) => m.id === value);
  const current = months.find((m) => m.id === value);

  const options = inList
    ? months
    : [{ id: value, label: value.replace("mo-", ""), published: !!published, is_current: false, pay_periods: [] }, ...months];

  return (
    <div className="flex items-center gap-2" data-testid="month-selector">
      <Button variant="outline" size="icon" className="rounded-lg hidden sm:inline-flex" onClick={prev} data-testid="month-prev" aria-label="Previous month">
        <ChevronLeft size={16} />
      </Button>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          className="w-[160px] sm:w-[300px] h-auto min-h-[40px] sm:min-h-[44px] py-1.5 rounded-lg [&>span]:!line-clamp-none [&>span]:w-full"
          data-testid="month-select"
        >
          <SelectValue placeholder="Select month" />
        </SelectTrigger>
        <SelectContent className="max-h-[440px]">
          {options.map((p) => (
            <SelectItem key={p.id} value={p.id} data-testid={`month-option-${p.id}`}>
              <div className="flex flex-col gap-0.5 w-full min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium truncate">{p.label}</span>
                  {p.is_current && <span className="text-[10px] uppercase tracking-widest text-blue-600 font-bold shrink-0">Current</span>}
                </div>
                <div className="hidden sm:block"><MonthMeta p={p} /></div>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button variant="outline" size="icon" className="rounded-lg hidden sm:inline-flex" onClick={next} data-testid="month-next" aria-label="Next month">
        <ChevronRight size={16} />
      </Button>
      <div className="ml-2 text-xs px-2 py-1 rounded-md border tabular-nums hidden sm:block"
           data-testid="month-status"
           style={{
              background: published ? "#DCFCE7" : "#F1F5F9",
              color: published ? "#166534" : "#475569",
              borderColor: published ? "#86EFAC" : "#CBD5E1",
           }}>
        {published ? "Published" : "Draft"}
      </div>
    </div>
  );
}
